import EnhancedCoreRegistry from './enhanced.core.registry.js';
import ElementIterator from '../helper/iterator.helper.js';
import './core.config.manager.js';

class PluginControlSingleton {

  constructor() {
    this.controlRegistry = new EnhancedCoreRegistry();
  }

  addPlugin(
    pluginIdentifier, pluginBlueprint, targetSelector = document,
    settings = {}) {
    if (this.controlRegistry.pluginExists(pluginIdentifier, targetSelector)) {
      throw new Error(`Plugin "${pluginIdentifier}" ist bereits registriert.`);
    }

    return this.controlRegistry.addPlugin(pluginIdentifier, pluginBlueprint,
      targetSelector, settings);
  }

  retrievePlugins() {
    return this.controlRegistry.getPluginNamesAndData();
  }

  fetchPlugin(pluginIdentifier, enforce = true) {
    if (!pluginIdentifier) {
      throw new Error('Es muss ein Plugin-Name übergeben werden!');
    }

    if (!this.controlRegistry.pluginExists(pluginIdentifier)) {
      if (enforce) {
        throw new Error(
          `Das Plugin "${pluginIdentifier}" ist nicht registriert. Möglicherweise müssen Sie es zuerst registrieren.`);
      } else {
        this.controlRegistry.addPlugin(pluginIdentifier);
      }
    }

    return this.controlRegistry.getPlugin(pluginIdentifier);
  }

  fetchPluginInstances(pluginIdentifier) {
    const plugin = this.fetchPlugin(pluginIdentifier);

    return plugin.get('instances');
  }

  static _queryElements(targetSelector) {
    if (targetSelector.startsWith('.')) {
      const regexEl = /^\.([\w-]+)$/.exec(targetSelector);
      if (regexEl) {
        return document.getElementsByClassName(regexEl[1]);
      }
    } else if (targetSelector.startsWith('#')) {
      const regexEl = /^#([\w-]+)$/.exec(targetSelector);
      if (regexEl) {
        const el = document.getElementById(regexEl[1]);

        return (el) ? [el] : [];
      }
    } else if (/^([\w-]+)$/.exec(targetSelector)) {
      return document.getElementsByTagName(targetSelector);
    }

    return document.querySelectorAll(targetSelector);
  }

  static retrievePluginInstanceFromElement(el, pluginIdentifier) {
    const instances = PluginControlSingleton.retrievePluginInstancesFromElement(
      el);

    return instances.get(pluginIdentifier);
  }

  static _isNode(el) {
    return el instanceof HTMLElement || el === document || el === window;
  }

  static retrievePluginInstancesFromElement(el) {
    if (!PluginControlSingleton._isNode(el)) {
      throw new Error('Passed element is not an Html element!');
    }

    el.pluginInstances = el.pluginInstances || new Map();

    return el.pluginInstances;
  }

  static instantiatePluginOnElement(
    el, pluginBlueprint, settings, pluginIdentifier) {
    if (typeof pluginBlueprint !== 'function') {
      throw new Error('Das übergebene Plugin ist keine Funktion oder Classe.');
    }

    const instance = PluginController.retrievePluginInstanceFromElement(el,
      pluginIdentifier);
    if (!instance) {
      return new pluginBlueprint(el, settings, pluginIdentifier);
    }

    return instance._update();
  }

  activatePlugin(
    pluginBlueprint, targetSelector, settings, pluginIdentifier = false) {
    if (PluginControlSingleton._isNode(targetSelector)) {
      return PluginControlSingleton.instantiatePluginOnElement(targetSelector,
        pluginBlueprint, settings, pluginIdentifier);
    }

    if (typeof targetSelector === 'string') {
      targetSelector = PluginControlSingleton._queryElements(targetSelector);
    }

    return ElementIterator.iterate(targetSelector, el => {
      PluginControlSingleton.instantiatePluginOnElement(el, pluginBlueprint,
        settings, pluginIdentifier);
    });
  }

  initializeAllPlugins() {
    const failures = [];
    ElementIterator.iterate(this.retrievePlugins(),
      (pluginData, pluginIdentifier) => {
        if (pluginIdentifier &&
          this.controlRegistry.pluginExists(pluginIdentifier)) {
          const plugin = this.controlRegistry.getPlugin(pluginIdentifier);
          if (plugin.has('registrations')) {
            ElementIterator.iterate(plugin.get('registrations'),
              registration => {
                try {
                  this.activatePlugin(plugin.get('class'),
                    registration.selector, registration.options,
                    plugin.get('name'));
                }
                catch (error) {
                  failures.push(error);
                }
              });
          }
        }
      });

    failures.forEach((error) => console.error(error));
  }

  deepMerge(...objects) {
    return objects.reduce((prev, obj) => {
      Object.keys(obj).forEach(key => {
        const pVal = prev[key];
        const oVal = obj[key];

        if (Array.isArray(pVal) && Array.isArray(oVal)) {
          prev[key] = pVal.concat(...oVal);
        } else {
          prev[key] = oVal;
        }
      });
      return prev;
    }, {});
  }

  _isNode(el) {
    return el instanceof HTMLElement || el === document || el === window;
  }

  initializePlugin(pluginIdentifier, selector, options) {
    let plugin;
    let pluginClass;
    let mergedOptions;

    if (this.controlRegistry.pluginExists(pluginIdentifier, selector)) {
      plugin = this.controlRegistry.getPlugin(pluginIdentifier);
      const registrationOptions = plugin.get('registrations').get(selector);
      pluginClass = plugin.get('class');
      mergedOptions = this.deepMerge(pluginClass.options || {},
        this.deepMerge(registrationOptions.options || {}, options || {}));
    } else {
      plugin = this.controlRegistry.getPlugin(pluginIdentifier);
      pluginClass = plugin.get('class');
      mergedOptions = this.deepMerge(pluginClass.options || {}, options || {});
    }

    this._initializePlugin(pluginClass, selector, mergedOptions,
      plugin.get('name'));
  }

  _initializePlugin(pluginClass, selector, options, pluginName = false) {
    if (this._isNode(selector)) {
      return PluginControlSingleton._initializePluginOnElement(selector,
        pluginClass, options, pluginName);
    }

    if (typeof selector === 'string') {
      selector = PluginControlSingleton._queryElements(selector);
    }

    return ElementIterator.iterate(selector, el => {
      PluginControlSingleton._initializePluginOnElement(el, pluginClass,
        options, pluginName);
    });
  }

  static _initializePluginOnElement(el, pluginClass, options, pluginName) {
    if (typeof pluginClass !== 'function') {
      throw new Error('The passed plugin is not a function or a class.');
    }

    const instance = PluginController.retrievePluginInstanceFromElement(el,
      pluginName);
    if (!instance) {
      return new pluginClass(el, options, pluginName);
    }

    return instance._update();
  }

  static getPluginInstanceFromElement(el, pluginName) {
    const instances = PluginControlSingleton.getPluginInstancesFromElement(el);

    return instances.get(pluginName);
  }

  static getPluginInstancesFromElement(el) {
    if (!this._isNode(el)) {
      throw new Error('Passed element is not an Html element!');
    }

    el.__plugins = el.__plugins || new Map();

    return el.__plugins;
  }
}

export const PluginControlInstance = Object.freeze(
  new PluginControlSingleton());

export default class PluginController {

  constructor() {
    window.PluginController = this;
  }

  static addPlugin(
    pluginIdentifier, pluginBlueprint, targetSelector = document,
    settings = {}) {
    return PluginControlInstance.addPlugin(pluginIdentifier, pluginBlueprint,
      targetSelector,
      settings);
  }

  static retrievePluginList() {
    return PluginControlInstance.retrievePlugins();
  }

  static fetchPlugin(pluginIdentifier) {
    return PluginControlInstance.fetchPlugin(pluginIdentifier);
  }

  static retrievePluginInstances(pluginIdentifier) {
    return PluginControlInstance.fetchPluginInstances(pluginIdentifier);
  }

  static retrievePluginInstanceFromElement(el, pluginIdentifier) {
    return PluginControlSingleton.retrievePluginInstanceFromElement(el,
      pluginIdentifier);
  }

  static retrievePluginInstancesFromElement(el) {
    return PluginControlSingleton.retrievePluginInstancesFromElement(el);
  }

  static initializeAllPlugins() {
    PluginControlInstance.initializeAllPlugins();
  }

  static initializePlugin(pluginIdentifier, targetSelector, settings) {
    PluginControlInstance.initializePlugin(pluginIdentifier, targetSelector,
      settings);
  }
}

window.PluginController = PluginController;
